import { CONFIG } from "../config.js";

/**
 * Google Sheets Service
 * Handles all interactions with Google Apps Script backend
 */
class GoogleSheetsService {
  constructor() {
    this.baseUrl = CONFIG.APPS_SCRIPT_URL;
    this.cache = {
      metadata: null,
      lastFetch: null,
      // Dashboard data cache
      dashboardData: null,
      dashboardFilters: null,
      dashboardLastFetch: null,
    };
    // Cache duration in milliseconds (2 minutes)
    this.DASHBOARD_CACHE_DURATION = 2 * 60 * 1000;
  }

  /**
   * Fetches dashboard data with optional filters
   * Uses caching to improve performance on repeated calls
   * @param {Object} filters - Filter parameters
   * @param {boolean} forceRefresh - Force refresh cache
   * @returns {Promise<Object>} Dashboard data
   */
  async getDashboardData(filters = {}, forceRefresh = false) {
    // Check cache first (unless force refresh)
    if (
      !forceRefresh &&
      this.cache.dashboardData &&
      this.cache.dashboardFilters
    ) {
      const cacheAge = Date.now() - this.cache.dashboardLastFetch;
      // Check if cached filters match current filters
      const filtersMatch =
        JSON.stringify(filters) === JSON.stringify(this.cache.dashboardFilters);
      if (filtersMatch && cacheAge < this.DASHBOARD_CACHE_DURATION) {
        console.log("Using cached dashboard data");
        return this.cache.dashboardData;
      }
    }

    try {
      const params = new URLSearchParams({
        action: CONFIG.API.GET_DASHBOARD,
        year: filters.year || "",
        month: filters.month || "",
        project: filters.project || "",
        accountType: filters.accountType || "",
      });

      const response = await fetch(`${this.baseUrl}?${params}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Failed to fetch dashboard data");
      }

      // Cache the result
      this.cache.dashboardData = result.data;
      this.cache.dashboardFilters = filters;
      this.cache.dashboardLastFetch = Date.now();

      return result.data;
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      throw new Error("ບໍ່ສາມາດໂຫຼດຂໍ້ມູນໄດ້: " + error.message);
    }
  }

  /**
   * Fetches metadata (projects, categories, currencies, account types)
   * @param {boolean} forceRefresh - Force refresh cache
   * @returns {Promise<Object>} Metadata object
   */
  async getMetadata(forceRefresh = false) {
    // Return cached data if available and not forcing refresh
    if (!forceRefresh && this.cache.metadata && this.cache.lastFetch) {
      const cacheAge = Date.now() - this.cache.lastFetch;
      if (cacheAge < 5 * 60 * 1000) {
        // Cache for 5 minutes
        return this.cache.metadata;
      }
    }

    try {
      const params = new URLSearchParams({
        action: CONFIG.API.GET_METADATA,
      });

      const response = await fetch(`${this.baseUrl}?${params}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Failed to fetch metadata");
      }

      // Cache the result
      this.cache.metadata = result.data;
      this.cache.lastFetch = Date.now();

      return result.data;
    } catch (error) {
      console.error("Error fetching metadata:", error);
      throw new Error("ບໍ່ສາມາດໂຫຼດຂໍ້ມູນຕັ້ງຄ່າໄດ້: " + error.message);
    }
  }

  /**
   * Read a File object as a base64 data URL
   * @param {File} file
   * @returns {Promise<{base64: string, fileName: string, mimeType: string}>}
   */
  readFileAsBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () =>
        resolve({
          base64: reader.result,
          fileName: file.name,
          mimeType: file.type,
        });
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  }

  /**
   * Upload files to Google Drive via Apps Script uploadFiles action
   * @param {FileList|File[]} files
   * @param {string} transactionDate
   * @param {string} itemName
   * @returns {Promise<string>} Comma-separated file URLs
   */
  async uploadFiles(files, transactionDate, itemName) {
    const filesArray = Array.from(files);
    const filesDataPromises = filesArray.map((f) => this.readFileAsBase64(f));
    const filesData = await Promise.all(filesDataPromises);

    const formData = new FormData();
    formData.append("action", "uploadFiles");
    formData.append("filesData", JSON.stringify(filesData));
    formData.append("transactionDate", transactionDate);
    formData.append("itemName", itemName);

    const response = await fetch(this.baseUrl, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.message || "File upload failed");
    }

    return result.fileUrls || "";
  }

  /**
   * Submit a new transaction to Google Sheets
   * Handles optional file attachment upload to Google Drive first,
   * then saves the transaction with the resulting DocumentLink and MoreInfo.
   * @param {Object} transaction - Transaction data
   * @param {File|null} attachmentFile - Optional file to upload
   * @returns {Promise<Object>} Result
   */
  async submitTransaction(transaction, attachmentFile = null) {
    try {
      let documentLink = "";

      // Upload file first if provided
      if (attachmentFile) {
        documentLink = await this.uploadFiles(
          [attachmentFile],
          transaction.date,
          transaction.item,
        );
      }

      const formData = new FormData();
      formData.append("action", "submitTransaction");
      formData.append("date", transaction.date);
      formData.append("type", transaction.type);
      formData.append("amount", transaction.amount);
      formData.append("currency", transaction.currency);
      formData.append("exchangeRate", transaction.exchangeRate || "");
      formData.append("category", transaction.category);
      formData.append("project", transaction.project || "");
      formData.append("accountType", transaction.accountType);
      formData.append("item", transaction.item);
      formData.append("moreInfo", transaction.moreInfo || "");
      formData.append("documentLink", documentLink);

      const response = await fetch(this.baseUrl, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Failed to submit transaction");
      }

      // Clear cache to force refresh on next data fetch
      this.clearCache();

      return result;
    } catch (error) {
      console.error("Error submitting transaction:", error);
      throw new Error("ບໍ່ສາມາດບັນທຶກລາຍການໄດ້: " + error.message);
    }
  }

  /**
   * Generic method to perform an action (Create/Delete)
   */
  async performAction(action, data) {
    try {
      const formData = new FormData();
      formData.append("action", action);

      // Append all data properties to form data
      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });

      const response = await fetch(this.baseUrl, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || `Failed to perform ${action}`);
      }

      this.clearCache(); // Clear cache on any modification
      return result;
    } catch (error) {
      console.error(`Error performing ${action}:`, error);
      throw new Error(error.message);
    }
  }

  // Projects
  async addProject(name) {
    return this.performAction("addProject", { name });
  }
  async deleteProject(name) {
    return this.performAction("deleteProject", { name });
  }

  // Categories
  async addCategory(name, type) {
    return this.performAction("addCategory", { name, type });
  }
  async deleteCategory(name, type) {
    return this.performAction("deleteCategory", { name, type });
  }

  // Account Types
  async addAccountType(name) {
    return this.performAction("addAccountType", { name });
  }
  async deleteAccountType(name) {
    return this.performAction("deleteAccountType", { name });
  }

  // Currencies
  async addCurrency(name, symbol) {
    return this.performAction("addCurrency", { name, symbol });
  }
  async deleteCurrency(name) {
    return this.performAction("deleteCurrency", { name });
  }

  // Transactions
  /**
   * Delete a transaction by row index
   * @param {number} rowIndex - The row index in the Google Sheet (1-based)
   * @returns {Promise<Object>} Result
   */
  async deleteTransaction(rowIndex) {
    try {
      if (!rowIndex || typeof rowIndex !== "number" || rowIndex <= 1) {
        throw new Error(
          "Invalid row index. Row must be a number greater than 1.",
        );
      }

      const params = new URLSearchParams({
        action: "deleteTransaction",
        rowIndex: rowIndex,
      });

      const response = await fetch(`${this.baseUrl}?${params}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Failed to delete transaction");
      }

      // Clear cache to force refresh on next data fetch
      this.clearCache();

      return result;
    } catch (error) {
      console.error("Error deleting transaction:", error);
      throw new Error("ບໍ່ສາມາດລຶບລາຍການໄດ້: " + error.message);
    }
  }

  /**
   * Clears all caches (metadata and dashboard data)
   */
  clearCache() {
    this.cache.metadata = null;
    this.cache.lastFetch = null;
    this.cache.dashboardData = null;
    this.cache.dashboardFilters = null;
    this.cache.dashboardLastFetch = null;
  }
}

// Export singleton instance
export const googleSheetsService = new GoogleSheetsService();
